import NextApp, { AppContext } from 'next/app';
import { IncomingMessage } from 'http';

import { BspProvider } from '@eventbrite/bsp-sdk/react';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';

import { useBspConfig } from '@/bsp/config';
import { Wrapper } from '@/components/_shared/Wrapper';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { ListingsAppProps } from '@/types';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';

import { buildListingsUserCustom, getStatsigEnvFromStage, useInitStatsig } from '../../common/statsig';
import { DatadogInit } from '../../common/monitoring/datadogrum';

import '@/styles/globals.css';

export default function App({ Component, pageProps, serverUrl, stage, statsigClientKey }: ListingsAppProps) {
  const statsigCustom = pageProps.context
    ? buildListingsUserCustom(pageProps.context, pageProps.userIsAuthenticated)
    : undefined;

  useInitStatsig({
    clientKey: statsigClientKey,
    stableId: pageProps.stableId,
    custom: statsigCustom,
    environmentTier: getStatsigEnvFromStage(stage),
  });

  const bspConfig = useBspConfig();
  const { locale = 'en-US', messages = {} } = pageProps;

  return (
    <BspProvider config={bspConfig}>
      <QueryClientProvider>
        <IntlProviderWrapper messages={messages} locale={locale}>
          <Wrapper serverUrl={serverUrl} stage={stage} locale={locale}>
            <DatadogInit stage={stage} />
            <ConsentManagerScripts />
            <Component {...pageProps} stage={stage} />
          </Wrapper>
        </IntlProviderWrapper>
      </QueryClientProvider>
    </BspProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage | undefined;
  const appProps = await NextApp.getInitialProps(context);

  const serverUrl = req ? getDomainUrl(req) : getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  const statsigClientKey = process.env.STATSIG_CLIENT_KEY ?? '';

  return {
    ...appProps,
    serverUrl,
    stage,
    statsigClientKey,
  };
};
